import axios from 'axios';
import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';
import { ENV } from '@/config/env';
import type { ContentDelivery } from './contentOrderService';
import { CopyleaksAiWarningError, type CopyleaksWarning } from './contentOrderService';

// Re-export for consumers that only import from orderService (BuyNowModal,
// OrdersPage, etc.). Single source of truth for the shape.
export { CopyleaksAiWarningError, type CopyleaksWarning };

/**
 * Thrown when a reject-content request is refused by the platform's
 * `order.max_revisions` cap (or by an admin flipping `revisions_allowed`
 * off for a specific order). Task's `ContentWorkflowService::enforceRevisionCap`
 * returns a rich 422 body:
 *   { message: "Maximum revisions reached — 3 of 3 already used for this order.",
 *     errors: { revision_notes: [...] },
 *     meta: { max_revisions, revision_count } }
 *
 * Consumers `catch (err instanceof RevisionCapExceededError)` and toast
 * `err.message` so users see the actual reason instead of "Request failed
 * with status code 422".
 */
export class RevisionCapExceededError extends Error {
  readonly maxRevisions: number | null;
  readonly revisionCount: number | null;
  constructor(message: string, meta?: { max_revisions?: number; revision_count?: number }) {
    super(message);
    this.name = 'RevisionCapExceededError';
    this.maxRevisions = meta?.max_revisions ?? null;
    this.revisionCount = meta?.revision_count ?? null;
  }
}

/**
 * Detect the enforceRevisionCap 422 shape on an axios error and rethrow
 * as a typed `RevisionCapExceededError`. Any other axios error (including
 * other 422s) is rethrown as-is.
 */
function rethrowRevisionCapError(err: unknown): never {
  if (axios.isAxiosError(err) && err.response?.status === 422) {
    const body = err.response.data as
      | {
          message?: string;
          errors?: { revision_notes?: unknown; content_ai_probability?: unknown };
          meta?: { max_revisions?: number; revision_count?: number };
        }
      | undefined;
    const isCap =
      body?.errors && 'revision_notes' in body.errors && !('content_ai_probability' in body.errors);
    if (isCap && body?.message) {
      throw new RevisionCapExceededError(body.message, body.meta);
    }
  }
  throw err as Error;
}

/**
 * Shape of `GET /v1/catalog/workflow` — live caps read from Settings so
 * the frontend never hardcodes them and picks up admin edits within a
 * React Query staleTime window.
 */
export interface OrderWorkflowConfig {
  maxRevisions: number;
  autoCancelHours: number;
}

export async function fetchOrderWorkflowConfig(): Promise<OrderWorkflowConfig> {
  const url = ENDPOINTS.ORDER_WORKFLOW_CONFIG;
  const response = await apiClient.get(url);
  const data = response.data?.data ?? response.data;
  return {
    maxRevisions: Number(data?.max_revisions ?? 3),
    autoCancelHours: Number(data?.auto_cancel_hours ?? 24),
  };
}

// `axios` (default import) is retained ONLY for the `axios.isAxiosError`
// type guard — it's a pure static helper with no interceptor side effects.
// All actual HTTP calls go through `apiClient`.

interface OrderActionResponse {
  success: boolean;
  message: string;
}

export interface CreateOrderPayload {
  site_id?: string;
  order_type: 'guest_post' | 'curated_link' | 'visual_link' | 'content';
  title: string;
  target_url?: string;
  anchor_text?: string;
  /** Existing blog/article URL on the publisher site to insert the link into.
   *  Required for `curated_link` (Link Insertion) and `visual_link` orders. */
  backlink_url?: string;
  publisher_id?: string;
  grey_niche_id?: string;
  includes_content?: boolean;
  /** Who writes the content. Omit to let backend default to "writer". Set
   *  to "advertiser" when advertiser_content is supplied. */
  content_by?: 'writer' | 'advertiser';
  content_word_count?: 800 | 1000 | 1200 | 1500 | 2000;
  content_brief?: string;
  advertiser_content?: string;
  keywords?: string[];
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  advertiser_notes?: string;
  description?: string;
  currency?: string;
  revisions_allowed?: boolean;
  /**
   * Set true on the re-post after the advertiser confirms the Copyleaks
   * AI-content warning popup. Only meaningful when `advertiser_content`
   * is present. Backend accepts it as `warnings_acknowledged`.
   */
  warningsAcknowledged?: boolean;
}

export interface CreateOrderResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    order_number: string;
    advertiser_id: string;
    publisher_id: string | null;
    site_id: string | null;
    order_type: string;
    title: string;
    target_url: string | null;
    anchor_text: string | null;
    published_url: string | null;
    status: string;
    payment_status: string;
    priority: string;
    includes_content: boolean;
    content_word_count: number | null;
    content_brief: string | null;
    advertiser_content: string | null;
    site_selling_price: number;
    content_selling_price: number;
    grey_niche_selling_price: number;
    total_amount: number;
    currency: string;
    due_date: string | null;
    advertiser_notes: string | null;
    created_at: string;
    updated_at: string;
  };
}

/* ── List Orders ── */

export interface MyOrdersParams {
  status?: string;
  /** Comma-separated list: e.g. "guest_post,curated_link" */
  order_type?: string;
  /** Comma-separated list: e.g. "guest_post,content" */
  package_type?: string;
  site_id?: string;
  has_available_slots?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  role?: 'advertiser' | 'publisher';
  per_page?: number;
  page?: number;
}

/** Grey niche relation (included when applicable) */
export interface GreyNicheRef {
  id: string;
  name: string;
}

/** Single order as returned by /my-orders (with `type: "order"`) */
export interface MyOrderItem {
  type: 'order';
  id: string;
  order_number: string;
  publisher_id: string | null;
  site_id: string | null;
  package_id?: string | null;
  package_order_id?: string | null;
  order_type: 'guest_post' | 'curated_link' | 'visual_link' | 'content';
  title: string;
  target_url: string | null;
  anchor_text: string | null;
  published_url: string | null;
  status: string;
  progress_pct?: number;
  payment_status: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  includes_content: boolean;
  content_by?: string | null;
  content_word_count: number | null;
  content_brief: string | null;
  advertiser_content: string | null;
  /** Revision requested on the writer's draft */
  content_revision_requested?: boolean;
  content_revision_reason?: string | null;
  content_revision_requested_at?: string | null;
  grey_niche_id: string | null;
  grey_niche?: GreyNicheRef | null;
  grey_niche_selling_price?: string | number | null;
  site_selling_price: string | number;
  content_selling_price?: string | number;
  total_amount: string | number;
  currency: string;
  paid_at?: string | null;
  due_date: string | null;
  confirmed_at?: string | null;
  started_at?: string | null;
  expected_at?: string | null;
  published_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancellation_reason?: string | null;
  refund_amount?: string | null;
  refunded_at?: string | null;
  sla_breached?: boolean;
  advertiser_rating: number | null;
  advertiser_feedback: string | null;
  revision_count: number;
  advertiser_notes: string | null;
  tags?: string[];
  task_id?: string | null;
  content_task_id?: string | null;
  site: { id: string; name: string; domain: string } | null;
  contents?: ContentDelivery[];
  /** Writer task + draft (when content_by === 'writer' and the writer has drafted) */
  content_task?: OrderContentTask | null;
  /** Publisher task handling live placement after content is approved. */
  publishing_task?: OrderPublishingTask | null;
  /** Flattened writer view — mirrors content_task with writer fields at top level */
  content_writer?: OrderContentWriter | null;
  created_at: string;
  updated_at: string;
}

/** Publisher-side task that takes the approved content live. */
export interface OrderPublishingTask {
  id: string;
  status: string;
  workflow_step: number;
  live_url: string | null;
}

/** Writer's task attached to an order — includes the latest draft */
export interface OrderContentTask {
  id: string;
  content_writer_id: string;
  content_status: string;
  written_content: string | null;
  content_writer: {
    id: string;
    keycloak_id?: string | null;
    name: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  } | null;
}

/** Flattened content-writer snapshot returned on each order */
export interface OrderContentWriter {
  id: string;
  keycloak_id?: string | null;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
  content_status?: string;
  written_content?: string | null;
}

/** Package row as returned inline by /my-orders (with `type: "package"`) */
export interface MyPackageItem {
  type: 'package';
  id: string;
  order_number: string | null;
  package_id: string;
  package_name: string;
  package_type: 'guest_post' | 'curated_link' | 'visual_link' | 'content';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  payment_status?: string;
  total_items: number;
  completed_items: number;
  guest_post_count?: number;
  curated_link_count?: number;
  visual_link_count?: number;
  content_count?: number;
  total_selling_price?: string;
  discount_type?: 'percentage' | 'fixed' | null;
  discount_amount?: string | null;
  promotion_code?: string | null;
  final_price: string;
  description?: string | null;
  client_notes?: string | null;
  expected_completion_date?: string | null;
  actual_completion_date?: string | null;
  advertiser_notes?: string | null;
  orders: MyOrderItem[];
  items?: MyPackageOrderItem[];
  created_at: string;
  updated_at: string;
}

/** A line-item inside a package order (pre-allocation) as returned by /my-orders */
export interface MyPackageOrderItem {
  id: string;
  package_order_id: string;
  target_url: string | null;
  anchor_text: string | null;
  topic: string | null;
  instruction: string | null;
  word_count: number | null;
  content_brief: string | null;
  grey_niche_id: string | null;
  grey_niche?: { id: string; name: string } | null;
  grey_niche_price: string | null;
  unit_price: string | null;
  total_price: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export type MyOrdersRow = MyOrderItem | MyPackageItem;

export interface MyOrdersMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface MyOrdersResponse {
  status: string;
  message: string;
  code: number;
  data: MyOrdersRow[];
  meta: MyOrdersMeta;
  links?: {
    first?: string | null;
    last?: string | null;
    prev?: string | null;
    next?: string | null;
  };
}

/**
 * GET /v1/orders/my-orders — Fetch advertiser's orders with filters & pagination.
 */
export async function fetchMyOrders(params: MyOrdersParams = {}): Promise<MyOrdersResponse> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  }
  const queryString = new URLSearchParams(
    Object.entries(query).map(([k, v]) => [k, String(v)])
  ).toString();
  const fullUrl = ENDPOINTS.MY_ORDERS + (queryString ? `?${queryString}` : '');
  const curl = `curl -X GET "${fullUrl}" \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[GET ${ENDPOINTS.MY_ORDERS}] curl:\n${curl}`);
  try {
    const response = await apiClient.get<MyOrdersResponse>(ENDPOINTS.MY_ORDERS, {
      params: query,
    });
    console.log(`[GET ${ENDPOINTS.MY_ORDERS}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[GET ${ENDPOINTS.MY_ORDERS}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

/* ── Order Detail ── */

export interface OrderLineItem {
  id: string;
  order_id: string;
  item_type: 'base_service' | 'addon' | string;
  name: string;
  quantity: number;
  unit_price: string;
  total_price: string;
  discount_amount: string | null;
  discount_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderStatusHistoryEntry {
  id: string;
  order_id: string;
  from_status: string | null;
  to_status: string;
  changed_by: string;
  notes: string | null;
  created_at: string;
}

export interface OrderTimelineEvent {
  id: string;
  order_id: string;
  step: string;
  label: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  sort_order: number;
  started_at: string | null;
  completed_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/** Full order detail returned by GET /v1/orders/{id} */
export interface OrderDetail extends MyOrderItem {
  /** Grey niche relation with name (snake_case from API) */
  grey_niche: GreyNicheRef | null;
  items: OrderLineItem[];
  status_history: OrderStatusHistoryEntry[];
  package_order: MyPackageItem | null;
  contents: ContentDelivery[];
  timeline_events: OrderTimelineEvent[];
}

/** GET /v1/orders/{id} — Fetch a single order detail with all relations. */
export async function fetchOrderDetail(
  orderId: string
): Promise<{ success: boolean; message: string; data: OrderDetail }> {
  const url = ENDPOINTS.ORDER_DETAIL(orderId);
  const curl = `curl -X GET ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[GET /v1/orders/${orderId}] curl:\n${curl}`);
  try {
    const response = await apiClient.get<{
      success: boolean;
      message: string;
      data: OrderDetail;
    }>(url);
    console.log(`[GET /v1/orders/${orderId}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[GET /v1/orders/${orderId}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

/**
 * Approve order content by providing the published URL.
 */
export async function approveOrder(
  orderId: string,
  publishedUrl: string
): Promise<OrderActionResponse> {
  const response = await apiClient.post<OrderActionResponse>(ENDPOINTS.ORDER_APPROVE(orderId), {
    published_url: publishedUrl,
  });
  return response.data;
}

/**
 * Reject order content by providing feedback (min 20 characters).
 */
export async function rejectOrder(orderId: string, feedback: string): Promise<OrderActionResponse> {
  const response = await apiClient.post<OrderActionResponse>(ENDPOINTS.ORDER_REJECT(orderId), {
    feedback,
  });
  return response.data;
}

/**
 * Complete a published order — releases escrow and finalizes the order.
 */
export async function completeOrder(orderId: string): Promise<OrderActionResponse> {
  const response = await apiClient.post<OrderActionResponse>(ENDPOINTS.ORDER_COMPLETE(orderId));
  return response.data;
}

/**
 * Cancel an order (only when order is cancellable).
 */
export async function cancelOrder(orderId: string, reason?: string): Promise<OrderActionResponse> {
  const response = await apiClient.post<OrderActionResponse>(
    ENDPOINTS.ORDER_CANCEL(orderId),
    reason ? { reason } : {}
  );
  return response.data;
}

export interface SlaStatusResponse {
  success: boolean;
  data: {
    order_id: string;
    sla_deadline: string;
    time_remaining: string;
    is_overdue: boolean;
  };
}

/**
 * Get SLA status for an order.
 */
export async function getOrderSlaStatus(orderId: string): Promise<SlaStatusResponse> {
  const response = await apiClient.get<SlaStatusResponse>(ENDPOINTS.ORDER_SLA_STATUS(orderId));
  return response.data;
}

export interface OrderHistoryEntry {
  id: string;
  action: string;
  actor: string;
  details: string;
  created_at: string;
}

export interface OrderHistoryResponse {
  success: boolean;
  data: OrderHistoryEntry[];
}

/**
 * Get the history/timeline for an order.
 */
export async function getOrderHistory(orderId: string): Promise<OrderHistoryResponse> {
  const response = await apiClient.get<OrderHistoryResponse>(ENDPOINTS.ORDER_HISTORY(orderId));
  return response.data;
}

export interface PayOrderResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    order_number: string;
    order_type: string;
    status: string;
    payment_status: string;
    paid_at: string;
    total_amount: number;
    currency: string;
    /** Minimal transaction — only id, amount, and post-debit wallet balance. */
    transaction: {
      id: string;
      amount: number;
      new_balance: number;
    };
  };
}

// Backend response shape for POST /v1/orders/{id}/pay (slim direct-debit model)
interface OrderPayApiResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    order_number: string;
    order_type?: string;
    status: string;
    payment_status: string;
    paid_at: string;
    total_amount: string | number;
    currency?: string;
    payment?: {
      payment_status: string;
      paid_at: string;
      currency: string;
      total_amount: string | number;
    };
    transaction: {
      id: string;
      amount: number | string;
      new_balance: number | string;
    };
  };
}

/**
 * POST /v1/orders/{id}/pay — direct-debit the advertiser's wallet.
 * Normalizes the `{ order, transaction }` response into the flat shape
 * PaymentModal consumes.
 */
export async function payForOrder(orderId: string): Promise<PayOrderResponse> {
  const url = ENDPOINTS.ORDER_PAY(orderId);
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[POST /v1/orders/${orderId}/pay] curl:\n${curl}`);
  let response;
  try {
    response = await apiClient.post<OrderPayApiResponse>(url, {});
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST /v1/orders/${orderId}/pay] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data,
        'errors:',
        (err.response?.data as { errors?: unknown })?.errors
      );
    }
    throw err;
  }
  console.log(`[POST /v1/orders/${orderId}/pay] response:`, response.data);
  const d = response.data.data;
  return {
    success: response.data.success,
    message: response.data.message,
    data: {
      id: d.id,
      order_number: d.order_number,
      order_type: d.order_type ?? '',
      status: d.status,
      payment_status: d.payment_status,
      paid_at: d.paid_at,
      total_amount: Number(d.total_amount) || 0,
      currency: d.currency ?? d.payment?.currency ?? 'USD',
      transaction: {
        id: d.transaction.id,
        amount: Number(d.transaction.amount) || 0,
        new_balance: Number(d.transaction.new_balance) || 0,
      },
    },
  };
}

/**
 * Place a new order (Buy Now from Browse Sites).
 *
 * When `advertiser_content` is set, the backend runs the Copyleaks AI
 * gate on it. On the confirm-required response (422 +
 * `requires_confirmation: true`), we throw `CopyleaksAiWarningError` so
 * the caller can open the popup and re-invoke with
 * `warningsAcknowledged: true`.
 */
export async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
  // Backend accepts `warnings_acknowledged` (snake_case) inside the body;
  // keep the friendlier camelCase field on the public API.
  const { warningsAcknowledged, ...rest } = payload;
  const body: Record<string, unknown> = {
    ...rest,
    ...(warningsAcknowledged ? { warnings_acknowledged: true } : {}),
  };
  try {
    const response = await apiClient.post<CreateOrderResponse>(ENDPOINTS.CREATE_ORDER, body);
    return response.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as
        | {
            requires_confirmation?: boolean;
            warnings?: CopyleaksWarning[];
            ai_report?: Record<string, unknown>;
          }
        | undefined;
      if (err.response?.status === 422 && data?.requires_confirmation === true) {
        throw new CopyleaksAiWarningError(data.warnings ?? [], data.ai_report ?? {});
      }
    }
    throw err;
  }
}

/* ── Content Pricing Tiers ── */

export interface ContentPricingTier {
  id: string;
  word_count: number;
  /**
   * Single tier price (retail). Backend collapsed the old
   * selling_price / buying_price split into this one field per the
   * 2026-08-22 platform-commission audit — writers are internal
   * contractors paid outside the app, so no wholesale split.
   * Stored as a string (Laravel decimal cast); consumers must Number()
   * before doing math.
   */
  price: string;
  is_active: boolean;
}

/**
 * Fetch all content pricing tiers.
 */
export async function fetchContentPricingTiers(): Promise<ContentPricingTier[]> {
  const response = await apiClient.get(ENDPOINTS.CONTENT_PRICING_TIERS);
  const tiers: ContentPricingTier[] = response.data?.data?.tiers ?? [];
  return tiers.filter((t) => t.is_active);
}

/* ── Advertiser review of writer's draft (content task) ────────────────── */

export interface ContentReviewActionResponse {
  success: boolean;
  message: string;
  data?: {
    id: string;
    content_status: string;
    status?: string;
    revision_count?: number;
    advertiser_reviewed_at?: string | null;
    advertiser_rejected_at?: string | null;
    rejection_reason?: string | null;
    updated_at?: string;
  };
}

/** POST /v1/tasks/{id}/advertiser-approve-content */
export async function advertiserApproveContent(
  taskId: string
): Promise<ContentReviewActionResponse> {
  const url = ENDPOINTS.TASK_ADVERTISER_APPROVE_CONTENT(taskId);
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[POST ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.post<ContentReviewActionResponse>(url, {});
    console.log(`[POST ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

export interface VerifyBacklinkResponse {
  success: boolean;
  message: string;
  data?: {
    id?: string;
    status?: string;
    live_url?: string;
    backlink_verified_at?: string | null;
    completed_at?: string | null;
    backlink_verification?: {
      status: 'passed' | 'failed' | 'pending_verification' | 'skipped';
      checks?: Record<string, boolean>;
      errors?: string[];
      meta?: Record<string, unknown>;
      checked_at?: string;
    };
    backlink_verification_attempts?: number;
    backlink_last_checked_at?: string;
  };
}

/** Shared response shape for order-side backlink approve/reject. */
export interface BacklinkActionResponse {
  success: boolean;
  message: string;
  data?: {
    order?: {
      id: string;
      link_verified?: boolean;
      link_verified_at?: string | null;
    };
    publishing_task?: {
      id: string;
      type?: string;
      status?: string;
      live_url?: string | null;
      backlink_verified_at?: string | null;
      completed_at?: string | null;
    };
  };
}

/** PATCH /v1/orders/{id}/content — advertiser updates their pre-written content. */
export interface UpdateAdvertiserContentResponse {
  success: boolean;
  message: string;
  data?: {
    id: string;
    advertiser_content: string;
    content_revision_requested?: boolean;
  };
}

/**
 * PATCH /v1/orders/{id}/content — advertiser updates their pre-written content.
 *
 * Runs through the Copyleaks AI gate. On the confirm-required response
 * (422 + `requires_confirmation: true`), throws `CopyleaksAiWarningError`
 * so the caller can open the popup and re-invoke with
 * `warningsAcknowledged: true`.
 */
export async function updateOrderAdvertiserContent(
  orderId: string,
  advertiserContent: string,
  options: { warningsAcknowledged?: boolean } = {}
): Promise<UpdateAdvertiserContentResponse> {
  const url = ENDPOINTS.ORDER_UPDATE_CONTENT(orderId);
  const body: Record<string, unknown> = {
    advertiser_content: advertiserContent,
    ...(options.warningsAcknowledged ? { warnings_acknowledged: true } : {}),
  };
  const curl = `curl -X PATCH ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
  console.log(`[PATCH ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.patch<UpdateAdvertiserContentResponse>(url, body);
    console.log(`[PATCH ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[PATCH ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
      const data = err.response?.data as
        | {
            requires_confirmation?: boolean;
            warnings?: CopyleaksWarning[];
            ai_report?: Record<string, unknown>;
          }
        | undefined;
      if (err.response?.status === 422 && data?.requires_confirmation === true) {
        throw new CopyleaksAiWarningError(data.warnings ?? [], data.ai_report ?? {});
      }
    }
    throw err;
  }
}

/** POST /v1/orders/{id}/approve-backlink — advertiser approves the live backlink. */
export async function approveOrderBacklink(orderId: string): Promise<BacklinkActionResponse> {
  const url = ENDPOINTS.ORDER_APPROVE_BACKLINK(orderId);
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[POST ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.post<BacklinkActionResponse>(url, {});
    console.log(`[POST ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

/** POST /v1/orders/{id}/reject-backlink — advertiser rejects the live backlink with a reason. */
export async function rejectOrderBacklink(
  orderId: string,
  reason: string
): Promise<BacklinkActionResponse> {
  const url = ENDPOINTS.ORDER_REJECT_BACKLINK(orderId);
  const body = { reason };
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
  console.log(`[POST ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.post<BacklinkActionResponse>(url, body);
    console.log(`[POST ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

/** POST /v1/tasks/{id}/workflow/verify-backlink — re-run backlink verification. */
export async function advertiserVerifyBacklink(taskId: string): Promise<VerifyBacklinkResponse> {
  const url = ENDPOINTS.TASK_VERIFY_BACKLINK(taskId);
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[POST ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.post<VerifyBacklinkResponse>(url, {});
    console.log(`[POST ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    throw err;
  }
}

/**
 * POST /v1/tasks/{id}/advertiser-reject-content
 *
 * On the platform's `order.max_revisions` cap being exceeded, throws
 * `RevisionCapExceededError` so callers can toast the exact backend
 * message ("Maximum revisions reached — X of Y already used ...").
 */
export async function advertiserRejectContent(
  taskId: string,
  reason: string
): Promise<ContentReviewActionResponse> {
  const url = ENDPOINTS.TASK_ADVERTISER_REJECT_CONTENT(taskId);
  const body = { reason };
  const curl = `curl -X POST ${url} \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
  console.log(`[POST ${url}] curl:\n${curl}`);
  try {
    const response = await apiClient.post<ContentReviewActionResponse>(url, body);
    console.log(`[POST ${url}] response:`, response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[POST ${url}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    rethrowRevisionCapError(err);
  }
}
